import React, { useEffect, useMemo, useState, useRef, Children, Fragment } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, MailIcon } from 'lucide-react';
import { Header } from '../components/Header';
import { SearchBar } from '../components/SearchBar';
import { TaskChip } from '../components/TaskChip';
import { ToolCard } from '../components/ToolCard';
import { tools, taskSuggestions } from '../data/tools';
import { SEO } from '../components/SEO';
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.18,
      delayChildren: 0.1
    }
  }
};
const headlineVariants = {
  hidden: {
    opacity: 0,
    y: 40
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 100,
      damping: 20,
      duration: 0.8
    }
  }
};
const subtitleVariants = {
  hidden: {
    opacity: 0,
    x: 60
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      type: 'tween',
      duration: 0.6,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};
const searchBarVariants = {
  hidden: {
    opacity: 0,
    scale: 0.85
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      type: 'spring',
      stiffness: 80,
      damping: 15
    }
  }
};
const chipContainerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.06
    }
  }
};
const chipVariants = {
  hidden: {
    opacity: 0,
    y: 16,
    scale: 0.9
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: 'spring',
      stiffness: 120,
      damping: 14
    }
  }
};
const nicheContainerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08
    }
  }
};
const nicheVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 120,
      damping: 14
    }
  }
};
const gridContainerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
      delayChildren: 0.1
    }
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.2
    }
  }
};
const gridItemVariants = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 100,
      damping: 18
    }
  },
  exit: {
    opacity: 0,
    y: -12,
    transition: {
      duration: 0.15
    }
  }
};
export function HomePage() {
  const [selectedNiche, setSelectedNiche] = useState('All');
  const [browseMode, setBrowseMode] = useState<'category' | 'task'>('task');
  const [selectedTask, setSelectedTask] = useState('All');
  const [highlightFilter, setHighlightFilter] = useState<string>('All');
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  const [pricingFilter, setPricingFilter] = useState<string>('All');
  const [pricingDropdownOpen, setPricingDropdownOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [newsletterSubmitted, setNewsletterSubmitted] = useState(false);
  const filterDropdownRef = useRef<HTMLDivElement>(null);
  const pricingDropdownRef = useRef<HTMLDivElement>(null);
  const ITEMS_PER_PAGE = 30;
  const trendingTools = tools.filter(tool => tool.trending);
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target as Node)) {
        setFilterDropdownOpen(false);
      }
      if (pricingDropdownRef.current && !pricingDropdownRef.current.contains(event.target as Node)) {
        setPricingDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const niches = [{
    label: 'All',
    value: 'All'
  }, {
    label: 'Students & Learners',
    value: 'Students'
  }, {
    label: 'Content Creators',
    value: 'Creators'
  }, {
    label: 'Teams & Workplaces',
    value: 'Teams'
  }, {
    label: 'Freelancers & Solopreneurs',
    value: 'Solo operators'
  }, {
    label: 'Developers & Builders',
    value: 'Developers'
  }];
  const tasks = [{
    label: 'All',
    value: 'All'
  }, {
    label: 'Write & Edit Content',
    value: 'Writing'
  }, {
    label: 'Create Visuals',
    value: 'Design'
  }, {
    label: 'Make Videos',
    value: 'Video'
  }, {
    label: 'Build & Code',
    value: 'Code'
  }, {
    label: 'Audio & Transcription',
    value: 'Audio'
  }, {
    label: 'Research & Organise',
    value: 'Research'
  }];
  const highlightFilters = [{
    label: 'All',
    emoji: '🔍',
    value: 'All'
  }, {
    label: 'New',
    emoji: '🆕',
    value: 'New'
  }, {
    label: 'Featured',
    emoji: '⭐',
    value: 'Featured'
  }, {
    label: 'Recommended',
    emoji: '👍',
    value: 'Recommended'
  }, {
    label: 'Trending',
    emoji: '📈',
    value: 'Trending'
  }];
  const pricingFilters = [{
    label: 'All',
    value: 'All'
  }, {
    label: 'Paid',
    value: 'Paid'
  }, {
    label: 'Unpaid',
    value: 'Unpaid'
  }];
  const filteredTools = browseMode === 'category' ? selectedNiche === 'All' ? tools : tools.filter(tool => {
    const niche = niches.find(n => n.label === selectedNiche);
    return tool.bestFor === niche?.value;
  }) : selectedTask === 'All' ? tools : tools.filter(tool => tool.category === selectedTask);
  const displayTools = useMemo(() => {
    let result = filteredTools;
    if (highlightFilter !== 'All') {
      result = result.filter(tool => {
        switch (highlightFilter) {
          case 'New':
            return tool.isNew;
          case 'Featured':
            return tool.featured;
          case 'Recommended':
            return tool.ease === 'Beginner';
          case 'Trending':
            return tool.trending;
          default:
            return true;
        }
      });
    }
    if (pricingFilter !== 'All') {
      result = result.filter(tool => {
        if (pricingFilter === 'Paid') return tool.pricing === 'Paid' || tool.pricing === 'Freemium';
        if (pricingFilter === 'Unpaid') return tool.pricing === 'Free';
        return true;
      });
    }
    return result;
  }, [filteredTools, highlightFilter, pricingFilter]);
  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedNiche, selectedTask, browseMode, highlightFilter, pricingFilter]);
  const totalPages = Math.max(1, Math.ceil(displayTools.length / ITEMS_PER_PAGE));
  const paginatedTools = displayTools.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  const centerOutwardChips = useMemo(() => {
    const mid = Math.floor(taskSuggestions.length / 2);
    return taskSuggestions.map((task, i) => ({
      task,
      order: Math.abs(i - mid)
    }));
  }, []);
  const jsonLdData = [{
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '4ALL AI',
    url: 'https://4all.ai',
    description: 'A free directory to discover AI tools for everyday life.',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://4all.ai/search?q={search_term_string}',
      'query-input': 'required name=search_term_string'
    }
  }, {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '4ALL AI',
    url: 'https://4all.ai',
    description: 'Free AI tool directory making artificial intelligence accessible to everyone.',
    sameAs: ['https://substack.com/@4allai']
  }, {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'AI Tools Directory',
    description: 'Curated collection of the best free AI tools',
    numberOfItems: tools.length,
    itemListElement: tools.slice(0, 10).map((tool, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: tool.name,
      url: tool.url
    }))
  }];
  return <div className="min-h-screen bg-white" data-id="element-334">
      <SEO title="Best Free AI Tools Directory — Discover AI for Everyday Life" description="Explore 50+ curated free AI tools for writing, design, video, coding, audio and research. Find the best AI tools for students, professionals and creators at 4ALL AI." keywords="AI tools, free AI tools, best AI tools 2026, AI tool directory, AI for beginners, AI writing tools, AI design tools, AI video tools, AI coding tools, AI audio tools, AI research tools, discover AI tools, AI tool finder, artificial intelligence tools, AI productivity tools" canonicalPath="/" jsonLd={jsonLdData} data-id="element-335" />
      <Header data-id="element-336" />

      <main className="w-full" data-id="element-337">
        {/* Hero Section */}
        <motion.section className="max-w-7xl mx-auto px-4 sm:px-6 pt-12 sm:pt-24 pb-2 sm:pb-4" variants={containerVariants} initial="hidden" animate="visible" data-id="element-338">
          <motion.div variants={headlineVariants} className="text-center mb-8 sm:mb-12" data-id="element-339">
            <h1 className="text-4xl sm:text-6xl md:text-7xl font-bold text-black mb-2 tracking-tight" data-id="element-340">
              <span className="bg-gradient-to-r from-cyan-200 via-purple-200 to-pink-200 bg-clip-text text-transparent" data-id="element-341">
                Welcome
              </span>{' '}
              to 4ALL
            </h1>
            <motion.p variants={subtitleVariants} className="text-base sm:text-lg text-grey-500 mt-3 sm:mt-4" data-id="element-342">
              AI for Life and Learning
            </motion.p>
            <motion.p variants={subtitleVariants} className="text-sm sm:text-base text-grey-400 mt-2" data-id="element-343">
              A free directory to discover AI tools for everyday life.
            </motion.p>
          </motion.div>

          <motion.div variants={searchBarVariants} data-id="element-344">
            <SearchBar size="large" data-id="element-345" />
          </motion.div>

          <div className="border-t border-grey-200 mt-8 sm:mt-12 mb-4" data-id="element-346" />

          <motion.div variants={nicheVariants} className="flex items-center justify-center mt-4 mb-4 pt-[30px] pb-[30px]" data-id="element-347">
            <div className="relative flex items-center bg-grey-100 rounded-full p-1" data-id="element-348">
              {[{
              label: 'Browse by Task',
              shortLabel: 'Task',
              value: 'task' as const
            }, {
              label: 'Browse by Category',
              shortLabel: 'Category',
              value: 'category' as const
            }].map(option => <button key={option.value} onClick={() => {
              setBrowseMode(option.value);
              if (option.value === 'category') setSelectedTask('All');else setSelectedNiche('All');
            }} className="relative z-10 text-xs sm:text-sm font-semibold uppercase tracking-wider px-3 sm:px-5 py-1.5 rounded-full transition-colors" data-id="element-349">
                  <span className={browseMode === option.value ? 'text-white' : 'text-grey-400'} data-id="element-350">
                    <span className="hidden sm:inline" data-id="element-351">{option.label}</span>
                    <span className="sm:hidden" data-id="element-352">{option.shortLabel}</span>
                  </span>
                  {browseMode === option.value && <motion.div layoutId="browse-mode-indicator" className="absolute inset-0 bg-black rounded-full" style={{
                zIndex: -1
              }} transition={{
                type: 'spring',
                stiffness: 400,
                damping: 30
              }} data-id="element-353" />}
                </button>)}
            </div>
          </motion.div>

          <AnimatePresence mode="wait" data-id="element-354">
            <motion.div key={browseMode} variants={nicheContainerVariants} initial="hidden" animate="visible" exit={{
            opacity: 0,
            transition: {
              duration: 0.15
            }
          }} className="flex flex-wrap justify-center gap-2 pb-1 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]" data-id="element-355">
              {browseMode === 'category' ? niches.map(niche => <motion.button key={niche.label} variants={nicheVariants} whileHover={{
              scale: 1.08,
              transition: {
                type: 'spring',
                stiffness: 300,
                damping: 15
              }
            }} whileTap={{
              scale: 0.95
            }} onClick={() => setSelectedNiche(niche.label)} className={`whitespace-nowrap px-3.5 sm:px-5 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm font-medium transition-colors border ${selectedNiche === niche.label ? 'border-black bg-black text-white' : 'border-grey-300 bg-transparent text-grey-700 hover:border-black hover:text-black'}`} data-id="element-356">
                      {niche.label}
                    </motion.button>) : tasks.map(task => <motion.button key={task.label} variants={nicheVariants} whileHover={{
              scale: 1.08,
              transition: {
                type: 'spring',
                stiffness: 300,
                damping: 15
              }
            }} whileTap={{
              scale: 0.95
            }} onClick={() => setSelectedTask(task.value)} className={`whitespace-nowrap px-3.5 sm:px-5 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm font-medium transition-colors border ${selectedTask === task.value ? 'border-black bg-black text-white' : 'border-grey-300 bg-transparent text-grey-700 hover:border-black hover:text-black'}`} data-id="element-357">
                      {task.label}
                    </motion.button>)}
            </motion.div>
          </AnimatePresence>
        </motion.section>

        {/* Tool Cards Grid */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-16" data-id="element-358">
          <div className="border-t border-grey-200 mb-8" data-id="element-359" />
          {/* Highlight Filter Dropdown */}
          <div className="flex flex-wrap items-center gap-3 mb-8" data-id="element-360">
            <span className="text-sm font-semibold text-black" data-id="element-361">Filter</span>
            <div className="relative inline-block" ref={filterDropdownRef} data-id="element-362">
              <button onClick={() => setFilterDropdownOpen(!filterDropdownOpen)} className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-grey-100 hover:bg-grey-200 text-sm font-medium text-grey-700 transition-colors" data-id="element-363">
                <span data-id="element-364">
                  {highlightFilters.find(f => f.value === highlightFilter)?.emoji}
                </span>
                <span data-id="element-365">
                  {highlightFilters.find(f => f.value === highlightFilter)?.label}
                </span>
                <ChevronDownIcon className={`w-4 h-4 transition-transform duration-200 ${filterDropdownOpen ? 'rotate-180' : ''}`} data-id="element-366" />
              </button>

              <AnimatePresence data-id="element-367">
                {filterDropdownOpen && <motion.div initial={{
                opacity: 0,
                y: -8,
                scale: 0.95
              }} animate={{
                opacity: 1,
                y: 0,
                scale: 1
              }} exit={{
                opacity: 0,
                y: -8,
                scale: 0.95
              }} transition={{
                duration: 0.15,
                ease: 'easeOut'
              }} className="absolute left-0 top-full mt-2 z-50 bg-white rounded-2xl shadow-lg border border-grey-200 py-2 min-w-[200px]" data-id="element-368">
                    {highlightFilters.map(filter => <button key={filter.value} onClick={() => {
                  setHighlightFilter(filter.value);
                  setFilterDropdownOpen(false);
                }} className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors text-left ${highlightFilter === filter.value ? 'bg-black text-white' : 'text-grey-700 hover:bg-grey-100'}`} data-id="element-369">
                        <span data-id="element-370">{filter.emoji}</span>
                        <span data-id="element-371">{filter.label}</span>
                      </button>)}
                  </motion.div>}
              </AnimatePresence>
            </div>

            <span className="text-sm font-semibold text-black" data-id="element-372">Sort</span>
            <div className="relative inline-block" ref={pricingDropdownRef} data-id="element-373">
              <button onClick={() => setPricingDropdownOpen(!pricingDropdownOpen)} className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-grey-100 hover:bg-grey-200 text-sm font-medium text-grey-700 transition-colors" data-id="element-374">
                <span data-id="element-375">
                  {pricingFilters.find(f => f.value === pricingFilter)?.label}
                </span>
                <ChevronDownIcon className={`w-4 h-4 transition-transform duration-200 ${pricingDropdownOpen ? 'rotate-180' : ''}`} data-id="element-376" />
              </button>

              <AnimatePresence data-id="element-377">
                {pricingDropdownOpen && <motion.div initial={{
                opacity: 0,
                y: -8,
                scale: 0.95
              }} animate={{
                opacity: 1,
                y: 0,
                scale: 1
              }} exit={{
                opacity: 0,
                y: -8,
                scale: 0.95
              }} transition={{
                duration: 0.15,
                ease: 'easeOut'
              }} className="absolute left-0 top-full mt-2 z-50 bg-white rounded-2xl shadow-lg border border-grey-200 py-2 min-w-[160px]" data-id="element-378">
                    {pricingFilters.map(filter => <button key={filter.value} onClick={() => {
                  setPricingFilter(filter.value);
                  setPricingDropdownOpen(false);
                }} className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors text-left ${pricingFilter === filter.value ? 'bg-black text-white' : 'text-grey-700 hover:bg-grey-100'}`} data-id="element-379">
                        <span data-id="element-380">{filter.label}</span>
                      </button>)}
                  </motion.div>}
              </AnimatePresence>
            </div>

            <a href="mailto:tools@4all.ai?subject=Tool%20Request" className="ml-auto flex items-center gap-2 px-4 py-2.5 rounded-full border border-grey-300 text-sm font-medium text-grey-700 hover:border-black hover:text-black transition-colors" data-id="element-381">
              Request a tool
            </a>
          </div>

          <AnimatePresence mode="wait" data-id="element-382">
            <motion.div key={browseMode === 'category' ? `${selectedNiche}-${highlightFilter}` : `task-${selectedTask}-${highlightFilter}`} variants={gridContainerVariants} initial="hidden" animate="visible" exit="exit" data-id="element-383">
              {displayTools.length === 0 ? <motion.div className="text-center py-12 sm:py-20" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} data-id="element-384">
                  <p className="text-base sm:text-lg text-grey-500" data-id="element-385">
                    {highlightFilter === 'All' ? `Tools for ${browseMode === 'category' ? selectedNiche : selectedTask} coming soon` : `No ${highlightFilter.toLowerCase()} tools found for this selection`}
                  </p>
                </motion.div> : <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" data-id="element-386">
                  {paginatedTools.map(tool => <motion.div key={tool.id} variants={gridItemVariants} data-id="element-387">
                      <ToolCard tool={tool} data-id="element-388" />
                    </motion.div>)}
                </div>}
            </motion.div>
          </AnimatePresence>

          {/* Pagination */}
          {displayTools.length > ITEMS_PER_PAGE && <div className="flex items-center justify-center gap-2 mt-10" data-id="element-389">
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="p-2 rounded-full hover:bg-grey-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed" aria-label="Previous page" data-id="element-390">
                <ChevronLeftIcon className="w-5 h-5 text-grey-600" data-id="element-391" />
              </button>

              {Array.from({
            length: totalPages
          }, (_, i) => i + 1).map(page => <button key={page} onClick={() => setCurrentPage(page)} className={`w-10 h-10 rounded-full text-sm font-semibold transition-colors ${currentPage === page ? 'bg-black text-white' : 'text-grey-600 hover:bg-grey-100'}`} data-id="element-392">
                  {page}
                </button>)}

              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="p-2 rounded-full hover:bg-grey-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed" aria-label="Next page" data-id="element-393">
                <ChevronRightIcon className="w-5 h-5 text-grey-600" data-id="element-394" />
              </button>
            </div>}

          <p className="text-center text-sm text-grey-400 mt-6" data-id="element-395">
            Showing {displayTools.length} of {tools.length} tools
          </p>
        </section>

        {/* Newsletter Signup */}
        <section className="w-full bg-grey-50 border-t border-grey-200" data-id="element-396">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20 text-center" data-id="element-397">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-black" data-id="element-398">
              <MailIcon className="h-5 w-5 text-white" data-id="element-399" />
            </div>
            <h3 className="text-lg sm:text-xl font-semibold text-grey-900 mb-2" data-id="element-400">
              Be the first to know
            </h3>
            <p className="text-sm text-grey-500 mb-6 max-w-sm mx-auto" data-id="element-401">
              Get notified when we add new AI tools to the directory. No spam,
              just the latest discoveries.
            </p>

            {newsletterSubmitted ? <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} className="flex flex-col items-center gap-2" data-id="element-402">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-black" data-id="element-403">
                  <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} data-id="element-404">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" data-id="element-405" />
                  </svg>
                </div>
                <p className="text-sm font-medium text-grey-900" data-id="element-406">
                  You're on the list!
                </p>
                <p className="text-xs text-grey-400" data-id="element-407">
                  We'll keep you posted on the latest tools.
                </p>
              </motion.div> : <a href="https://substack.com/@4allai?r=798l30&utm_medium=ios&utm_source=stories&shareImageVariant=image" target="_blank" rel="noopener noreferrer" className="inline-block rounded-full bg-black px-8 py-3 text-sm font-medium text-white hover:bg-grey-800 transition-colors" data-id="element-408">
                Subscribe to updates
              </a>}
          </motion.div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-grey-200 mt-6 sm:mt-12" data-id="element-409">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8" data-id="element-410">
          <div className="mb-4 sm:mb-6 pb-4 sm:pb-6 border-b border-grey-200" data-id="element-411">
            <p className="text-xs text-grey-400 leading-relaxed" data-id="element-412">
              <span className="font-semibold text-grey-500" data-id="element-413">Disclaimer:</span>{' '}
              4ALL AI is an independent platform that curates and organises
              third-party AI tools for informational purposes only. We do not
              own, operate, or endorse any of the tools listed. Tool
              availability, pricing, and features may change without notice. Use
              of any third-party tool is subject to that tool's own terms and
              privacy policy. 4ALL AI is not responsible for any outcomes
              resulting from the use of listed tools. Always review a tool's
              terms before use.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-grey-500" data-id="element-414">
            <p className="text-center sm:text-left" data-id="element-415">
              © 2026 4ALL AI | All Rights Reserved by Skeuomorphic xyz
            </p>
            <div className="flex items-center gap-6" data-id="element-416">
              <Link to="/privacy" className="hover:text-black transition-colors" data-id="element-417">
                Privacy
              </Link>
              <Link to="/terms" className="hover:text-black transition-colors" data-id="element-418">
                Terms
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>;
}